/**
 * REGISTRE DE FAITS D'ENTREPRISE — source de vérité unique.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * POURQUOI CE FICHIER EXISTE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `entreprise.ts` avait déjà réglé le cas de l'adresse : un fait, un endroit.
 * Il n'a réglé que celui-là. Un audit du HTML servi en production, le
 * 29 août 2026, a relevé que chaque autre fait d'entreprise existait encore en
 * TROIS exemplaires — rendu React, JSON-LD, `llms.txt` — écrits à trois moments
 * différents par trois personnes différentes. Ils avaient divergé :
 *
 *   · « Assurance TOUS RISQUES incluse » sur la page, « assurance DE BASE
 *     incluse » dans llms.txt. Deux promesses commerciales distinctes, servies
 *     le même jour, sur le même domaine.
 *   · Cinq pays desservis dans le JSON-LD, neuf dans la FAQ, « cinq + le reste
 *     de l'Europe » dans llms.txt.
 *   · Vingt-deux communes listées sur l'accueil, quinze dans llms.txt, dont
 *     cinq absentes de l'accueil.
 *   · La grille tarifaire du garde-meubles recopiée à la main dans QUATRE
 *     fichiers.
 *
 * Corriger ces divergences une par une ne règle rien : elles reviennent à la
 * prochaine retouche de contenu. Ce fichier est la correction ; le reste n'est
 * que du nettoyage.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * CE QU'IL N'EST PAS
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Ce n'est PAS une troisième copie des données. Les faits qui avaient déjà un
 * domicile unique y restent, et le registre les référence :
 *
 *   identité, adresse, horaires   `entreprise.ts`
 *   communes et distances         `communes.json`, via `communes.ts`
 *   catalogue des prestations     `services.json`
 *
 * Le registre héberge en propre les faits qui n'avaient AUCUN domicile — ceux
 * qui vivaient dispersés dans du JSX, du JSON-LD et un fichier Markdown écrit à
 * la main : l'assurance, les statistiques, les pays desservis, les rôles des
 * lieux, la grille du garde-meubles.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * LES QUATRE SORTIES
 * ────────────────────────────────────────────────────────────────────────────
 *
 *   1. le rendu React            composants d'affichage
 *   2. le JSON-LD                `SchemaOrg.tsx`
 *   3. `llms.txt`                GÉNÉRÉ au build — `scripts/generer-llms.mjs`
 *   4. les outils WebMCP         `src/agentic/site/`
 *
 * `npm run test:coherence` compare ces quatre sorties fait par fait et ÉCHOUE
 * si deux d'entre elles divergent sur une même clé. C'est ce test, et non les
 * corrections ponctuelles, qui empêche la divergence de revenir.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * LA RÈGLE QUI COMPTE : NE JAMAIS INVENTER UN FAIT
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Chaque fait porte un `statut`, et la règle tient en une ligne : un fait dont
 * AUCUNE source ne peut être nommée ne va pas aux machines.
 *
 * `'a-confirmer'` n'est donc pas une coquetterie documentaire. Un fait dans cet
 * état est retiré des surfaces destinées aux MACHINES — llms.txt, JSON-LD,
 * outils WebMCP — parce que c'est là qu'une affirmation non vérifiée se propage
 * sans que personne la relise. Il reste visible sur la page, où il l'est déjà :
 * l'en retirer serait une décision éditoriale, qui appartient à Gramme et pas
 * au code.
 *
 * `question` est autre chose, et vit indépendamment du statut : un fait
 * parfaitement publié et sourcé peut demander une confirmation — c'est le cas
 * de l'assurance, dont la formulation est publiée depuis longtemps mais dont
 * seule Gramme connaît la portée contractuelle. `docs/QUESTIONS_OUVERTES.md`
 * est généré à partir de ce champ, et le plan correctif demande explicitement
 * de laisser ces points OUVERTS plutôt que de les trancher.
 *
 * `npm run test:coherence` échoue si un fait `'a-confirmer'` fuit vers une
 * surface machine. Une case vide est réparable ; un fait faux servi à un
 * assistant ne l'est pas — il est répété à des gens qui ne remonteront jamais
 * à la source.
 */
import { anneesExperience } from '../lib/anciennete';
import { ENTREPRISE } from './entreprise';
import donnees from './registre.json';

// ---------------------------------------------------------------------------
// Le type d'un fait
// ---------------------------------------------------------------------------

/**
 * Provenance d'un fait, sur le modèle déjà en place pour les autorisations de
 * stationnement des pages communes (`AutorisationStationnement`).
 *
 * `autorite` est CELUI QUI AFFIRME, pas celui qui republie. Pour un fait
 * d'entreprise c'est Gramme ; pour un règlement communal, la commune. La
 * distinction n'est pas théorique : elle décide qui l'on rappelle quand le fait
 * se révèle faux.
 */
export interface SourceFait {
  autorite: string;
  /** Service ou support précis, quand l'autorité en a plusieurs. */
  service?: string;
  /**
   * COMMENT le chiffre a été obtenu, quand il vient d'une mesure et non d'un
   * document : « enquête interne 2025, 180 répondants », « moyenne des
   * plannings sur douze mois ».
   *
   * L'addendum du plan correctif le demande explicitement pour les chiffres
   * commerciaux : une affirmation, un organisme OU une méthode, une date. Sans
   * l'un des deux derniers, « supérieure à 95% » ne se défend pas à côté d'une
   * note Google de 4,0 affichée sur la même page.
   */
  methode?: string;
  /** Page ou document d'où le fait est repris, s'il est en ligne. */
  url?: string;
  /** Date du relevé, AAAA-MM-JJ. Un fait sans date n'est pas vérifiable. */
  dateVerification: string;
}

/**
 * `'verifie'` : une source est nommée et datée. Le fait peut aller partout,
 *               surfaces machine comprises.
 * `'a-confirmer'` : le fait est affiché quelque part sur le site, et PERSONNE
 *               ne peut dire d'où il vient. Il reste sur la page, il ne va pas
 *               aux machines.
 *
 * Le statut ne dit pas si une question reste ouverte — un fait peut être
 * correctement sourcé sur le site ET mériter une confirmation de Gramme. C'est
 * `question` qui porte cela, indépendamment. Confondre les deux conduirait à
 * retirer des surfaces machine des faits parfaitement publiés, ou à y laisser
 * des chiffres dont personne ne répond.
 */
export type StatutFait = 'verifie' | 'a-confirmer';

export interface Fait<T = unknown> {
  /** Clé stable. C'est elle que compare le test de cohérence, jamais le texte. */
  cle: string;
  /** Valeur typée — c'est elle qui alimente le JSON-LD et les outils. */
  valeur: T;
  /** Formulation courte, destinée à l'affichage. Une puce, une pastille. */
  court: string;
  /** Formulation longue, destinée aux agents et à llms.txt. Une phrase entière. */
  long: string;
  source: SourceFait | null;
  statut: StatutFait;
  /**
   * Ce qu'il reste à demander à Gramme, s'il y a lieu.
   *
   * Obligatoire quand `statut` vaut `'a-confirmer'` — un fait douteux dont
   * personne n'a formulé la question est un fait douteux que personne n'ira
   * lever. Facultatif sinon : un fait publié et sourcé peut appeler une
   * confirmation contractuelle sans cesser d'être publiable.
   *
   * `docs/QUESTIONS_OUVERTES.md` est généré à partir de ce champ.
   */
  question?: string;
}

/** Rôles possibles d'un lieu. Énumérés : « le dépôt » et « le siège » sont deux. */
export type RoleLieu =
  | 'siege'
  | 'depot'
  | 'garde_meubles'
  | 'ancienne_implantation'
  | 'zone_desservie';

export interface Lieu {
  role: RoleLieu;
  /**
   * Commune, quand elle est ÉTABLIE. `null` tant que Gramme ne l'a pas
   * confirmée — et `null` se publie très bien : « notre dépôt » sans nom reste
   * vrai, alors qu'un nom supposé ne l'est peut-être pas.
   */
  localite: string | null;
  /** Rue et numéro, quand ils sont établis. `null` tant qu'ils ne le sont pas. */
  rue: string | null;
  codePostal: string | null;
  /** Ce que ce lieu fait, en une phrase. */
  fonction: string;
  /**
   * Statut PROPRE à ce lieu.
   *
   * Le registre porte un statut par FAIT ; il en faut un par lieu, parce que
   * `lieux` mélange l'établi et le supposé. Le siège social figure sur les
   * mentions légales ; l'emplacement du dépôt, non. Un statut unique pour la
   * liste entière obligerait à choisir entre taire le siège et affirmer le
   * dépôt — les deux sont faux.
   *
   * Seuls les lieux `'verifie'` sont nommés sur les surfaces machine.
   */
  statut: StatutFait;
  /**
   * QUI dit que ce lieu est là. Obligatoire dès que `statut` vaut `'verifie'`.
   *
   * C'est le garde-fou qui manquait, et son absence a coûté une erreur servie
   * en production : « Oupeye » a été inscrit sur la foi d'un commentaire de
   * code et d'une concordance de distances, sans que rien n'oblige à écrire
   * d'où venait le nom. Écrire une source, c'est se demander qui l'affirme —
   * et se rendre compte, à ce moment-là, que personne ne l'affirmait.
   *
   * `tests/registre.test.ts` échoue sur un lieu vérifié sans source.
   */
  source: SourceFait | null;
  /** Ce qu'il reste à demander à Gramme pour ce lieu précis. */
  question?: string;
}

export interface PalierTarifaire {
  /** Volume du box, en m³. `null` pour le palier « au-delà de 30 m³ ». */
  volumeM3: number | null;
  /** Libellé affiché — « 8 m³ », « +30 m³ ». */
  libelle: string;
  prixEuros: number;
  /** Unité de facturation : le palier au-delà de 30 m³ est au m³. */
  unite: 'mois' | 'm3-mois';
}
// ---------------------------------------------------------------------------
// Le contrat de forme
//
// Les valeurs viennent maintenant d'une base de données. Elles n'ont donc plus
// de type déduit par le compilateur : `registre.json` est du JSON, et TypeScript
// n'y lit que `string`, `number`, `unknown[]`.
//
// Cette table rétablit le typage, et fait mieux que le rétablir : elle DÉCLARE
// ce que chaque clé doit contenir, au lieu de le déduire de ce qu'on y avait
// écrit. C'est ce qui permet à `valeurDe('tarifs.garde_meubles')` de rendre un
// `PalierTarifaire[]` — et à `verifierForme()` de refuser une ligne de base qui
// n'en est pas un.
// ---------------------------------------------------------------------------

export interface ValeursRegistre {
  'entreprise.fondation': number;
  'entreprise.qualite': string;
  'devis.delai': string;
  'assurance.incluse': boolean;
  'assurance.tous_risques': 'option' | 'inclus';
  'assurance.garde_meubles': string[];
  'horaires.semaine': typeof ENTREPRISE.horaires;
  'horaires.samedi': null;
  'horaires.dimanche': 'ferme';
  'lieux': Lieu[];
  'couverture.pays': string[];
  'couverture.belgique': 'integrale';
  'stats.anciennete': number;
  'stats.demenagements_par_mois': number;
  'stats.capacite_m3': number;
  'stats.satisfaction': number;
  'avis.google': { note: number; nombre: number; url: string };
  'tarifs.garde_meubles': PalierTarifaire[];
  'tarifs.garde_meubles.duree_minimum': string;
  'tarifs.demenagement': 'sur-devis';
}

export type CleFait = keyof ValeursRegistre;

// ---------------------------------------------------------------------------
// Les faits DÉRIVÉS
//
// Deux valeurs ne peuvent pas être stockées : elles se périment.
//
// `stats.anciennete` figée en base vaudrait 78 jusqu'au 1er janvier, puis
// mentirait pendant un an. C'est exactement le défaut qui a laissé « 75 ans
// d'expérience » sur le site pendant trois ans, alors que la même page
// calculait l'ancienneté correctement à deux endroits.
//
// `horaires.semaine` a un domicile antérieur — `entreprise.ts` — d'où viennent
// aussi le pied de page, la page contact et le JSON-LD. Le registre le
// référence plutôt que de le recopier : deux copies d'un horaire, c'est
// précisément le point de départ de tout ce chantier.
//
// Ces valeurs sont recalculées à la LECTURE. Ce que la base en contient est
// une copie de confort, montrée dans le back-office ; c'est le calcul qui fait
// foi, et un test vérifie que les deux coïncident — sans quoi Gramme verrait
// dans l'admin un chiffre que le site ne sert pas.
// ---------------------------------------------------------------------------

const DERIVES: { [K in CleFait]?: () => ValeursRegistre[K] } = {
  'stats.anciennete': () => anneesExperience(),
  'horaires.semaine': () => ENTREPRISE.horaires,
};

/**
 * Les clés dont la valeur est CALCULÉE, et non lue en base.
 *
 * Exportée d'ici, et d'ici seulement. Le back-office en a besoin — il doit
 * afficher ces champs en lecture seule, parce que les modifier ne changerait
 * rien au site et que laisser croire le contraire est pire que de ne pas
 * afficher la ligne.
 *
 * Une première version en tenait une SECONDE copie dans `admin/lib/faits.ts`,
 * avec un test pour vérifier que les deux coïncidaient. C'était refaire, dans
 * la correction, la faute que la correction combat — et le test a d'ailleurs
 * cassé la CI en tirant le client Supabase dans une suite qui n'en a pas.
 * Une liste, un endroit, rien à comparer.
 */
export const CLES_DERIVEES = Object.keys(DERIVES) as CleFait[];

/** Cette clé est-elle calculée par le site plutôt que lue en base ? */
export const estDerivee = (cle: string): boolean =>
  (CLES_DERIVEES as readonly string[]).includes(cle);

// ---------------------------------------------------------------------------
// Chargement
// ---------------------------------------------------------------------------

interface FaitBrut {
  cle: string;
  valeur: unknown;
  court: string;
  long: string;
  source: SourceFait | null;
  statut: string;
  question?: string;
}

/**
 * Contrôles appliqués à CHAQUE chargement, y compris en production.
 *
 * Une constante TypeScript ne pouvait pas être malformée : le compilateur s'en
 * chargeait. Une table le peut — une valeur effacée depuis le back-office, une
 * ligne supprimée, un statut mal saisi. Ce qui était garanti gratuitement doit
 * maintenant être vérifié.
 *
 * L'échec est BRUYANT et immédiat : le pré-rendu s'arrête, donc le build, donc
 * le déploiement. Un registre à moitié chargé servirait des pages où un fait
 * sur deux manque, sans que rien ne le signale — et c'est le genre de panne
 * qu'on découvre trois semaines plus tard, par un audit.
 */
function verifierForme(faits: FaitBrut[]): void {
  const vues = new Set(faits.map((f) => f.cle));

  for (const cle of Object.keys(CONTRAT) as CleFait[]) {
    if (!vues.has(cle)) {
      throw new Error(
        `registre : le fait « ${cle} » est absent de registre.json. La table ` +
        '`company_facts` a-t-elle perdu une ligne, ou la politique RLS de lecture ' +
        'a-t-elle changé ? Voir scripts/sync-registre.mjs.',
      );
    }
  }

  for (const f of faits) {
    if (!(f.cle in CONTRAT)) {
      throw new Error(
        `registre : la clé « ${f.cle} » n'est pas déclarée dans ValeursRegistre. ` +
        'Une clé que le code ne connaît pas n’est lue par personne : ajoute-la au ' +
        'contrat, ou retire-la de la table.',
      );
    }
    if (f.statut !== 'verifie' && f.statut !== 'a-confirmer') {
      throw new Error(`registre : statut inconnu « ${f.statut} » sur « ${f.cle} ».`);
    }
    if (f.statut === 'a-confirmer' && !f.question) {
      // La règle qui était portée par la fabrique `fait()`. Un fait douteux
      // dont personne n'a formulé la question est un fait douteux que personne
      // n'ira lever.
      throw new Error(`registre : « ${f.cle} » en 'a-confirmer' sans question à poser.`);
    }
    if (!f.court?.trim()) {
      throw new Error(`registre : « ${f.cle} » sans formulation courte.`);
    }
  }
}

/**
 * Les clés attendues, sous une forme interrogeable à l'exécution.
 *
 * `ValeursRegistre` est un type : il disparaît à la compilation, et ne peut
 * donc pas servir à vérifier ce que la base a renvoyé. Cette liste est son
 * pendant à l'exécution, et `satisfies` garantit que les deux ne divergent
 * pas — ajouter une clé au type sans l'ajouter ici ne compile pas.
 */
const CONTRAT = {
  'entreprise.fondation': true, 'entreprise.qualite': true, 'devis.delai': true,
  'assurance.incluse': true, 'assurance.tous_risques': true, 'assurance.garde_meubles': true,
  'horaires.semaine': true, 'horaires.samedi': true, 'horaires.dimanche': true,
  'lieux': true, 'couverture.pays': true, 'couverture.belgique': true,
  'stats.anciennete': true, 'stats.demenagements_par_mois': true,
  'stats.capacite_m3': true, 'stats.satisfaction': true, 'avis.google': true,
  'tarifs.garde_meubles': true, 'tarifs.garde_meubles.duree_minimum': true,
  'tarifs.demenagement': true,
} as const satisfies Record<CleFait, true>;

function charger(): Record<CleFait, Fait> {
  const bruts = (donnees as { faits: FaitBrut[] }).faits;
  verifierForme(bruts);

  const registre = {} as Record<CleFait, Fait>;
  for (const brut of bruts) {
    const cle = brut.cle as CleFait;
    const derive = DERIVES[cle];
    registre[cle] = {
      ...brut,
      cle,
      statut: brut.statut as StatutFait,
      // La valeur calculée l'emporte sur la valeur stockée. Voir DERIVES.
      valeur: derive ? derive() : brut.valeur,
    };
  }
  return registre;
}

/**
 * LE REGISTRE.
 *
 * Chargé une fois, à l'import. La forme est vérifiée à ce moment-là : un
 * `registre.json` incomplet fait échouer le pré-rendu plutôt que de produire
 * des pages où un fait sur deux manque.
 */
export const REGISTRE: Record<CleFait, Fait> = charger();

// ---------------------------------------------------------------------------
// Accès
// ---------------------------------------------------------------------------

/** Le fait complet, avec sa valeur typée par le contrat. */
export function faitDe<K extends CleFait>(cle: K): Fait<ValeursRegistre[K]> {
  return REGISTRE[cle] as Fait<ValeursRegistre[K]>;
}

/**
 * La valeur typée d'un fait.
 *
 * Le type vient du CONTRAT (`ValeursRegistre`), et non plus de ce qui était
 * écrit dans le registre : la donnée arrive d'une base, et une base ne prouve
 * rien au compilateur. C'est `verifierForme()` qui garantit à l'exécution que
 * la clé existe ; le contrat garantit à la compilation ce qu'elle contient.
 *
 * L'assertion est donc assumée, et c'est le seul endroit du fichier où elle
 * l'est — tout le reste du code s'appuie dessus sans jamais reconvertir.
 */
export function valeurDe<K extends CleFait>(cle: K): ValeursRegistre[K] {
  return REGISTRE[cle].valeur as ValeursRegistre[K];
}

/** Formulation d'affichage. C'est ce que les composants React doivent rendre. */
export function courtDe(cle: CleFait): string {
  return REGISTRE[cle].court;
}

/** Formulation longue. C'est ce que lisent llms.txt et les outils WebMCP. */
export function longDe(cle: CleFait): string {
  return REGISTRE[cle].long;
}

/**
 * Un fait peut-il aller sur une surface destinée aux machines ?
 *
 * Non si son statut est `'a-confirmer'`. C'est la règle la plus simple du
 * registre, et elle est volontairement brutale : pas de niveau de confiance
 * intermédiaire, pas de « probablement ». Un assistant qui répète un fait le
 * présente comme établi, quelle que soit la nuance qu'on aurait mise autour.
 */
export function publiableAuxAgents(cle: CleFait): boolean {
  return REGISTRE[cle].statut === 'verifie';
}

/**
 * Un fait peut-il être AFFICHÉ, tout court ?
 *
 * ────────────────────────────────────────────────────────────────────────────
 * LA RÈGLE A CHANGÉ, ET C'EST L'ADDENDUM QUI L'A CHANGÉE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * La première version de ce registre laissait un fait non sourcé s'afficher sur
 * la page, en le coupant seulement des surfaces machine. Le raisonnement :
 * retirer une affirmation d'une page commerciale est une décision éditoriale
 * qui appartient à Gramme, pas au code.
 *
 * L'addendum du plan correctif tranche autrement, et il a raison sur un point
 * que ce raisonnement manquait : « satisfaction supérieure à 95% » affiché à
 * quinze centimètres d'une note Google de 4,0 sur 24 avis n'est pas un
 * argument commercial qu'on préserve, c'est un handicap qu'on laisse en place.
 * Le lecteur qui fait la soustraction conclut que le site enjolive.
 *
 * La règle devient donc : **un fait CHIFFRÉ porte sa source et sa date, ou il
 * n'est pas publié — nulle part.** C'est aussi ce qui rend le lot B durable :
 * un compteur dont la valeur n'est pas sourcée n'a rien à afficher.
 *
 * Elle ne s'applique qu'aux chiffres, et c'est délibéré. Une formulation
 * qualitative se discute et se nuance ; un chiffre se vérifie ou ne se vérifie
 * pas, et c'est lui qu'on cite.
 */
export function affichable(cle: CleFait): boolean {
  const fait = REGISTRE[cle];
  if (typeof fait.valeur !== 'number') return true;
  return fait.source !== null;
}

/** Les faits chiffrés qui ont le droit d'être affichés. */
export function chiffresAffichables(cles: readonly CleFait[]): CleFait[] {
  return cles.filter(affichable);
}

/** Les faits qui portent une question, pour `docs/QUESTIONS_OUVERTES.md`. */
export function faitsOuverts(): Fait[] {
  return (Object.values(REGISTRE) as Fait[]).filter((f) => f.question);
}

/** Toutes les clés, pour le test de cohérence. */
export const CLES: readonly CleFait[] = Object.keys(CONTRAT) as CleFait[];

// ---------------------------------------------------------------------------
// Projections d'usage courant
// ---------------------------------------------------------------------------

/** Le lieu qui tient un rôle donné, ou `undefined`. */
export function lieu(role: RoleLieu): Lieu | undefined {
  return valeurDe('lieux').find((l) => l.role === role);
}

/**
 * Le dépôt — d'où partent les camions, et d'où sont mesurées les distances.
 *
 * Sa `localite` vaut `null` : elle n'est pas confirmée. Tout ce qui l'affiche
 * doit donc traiter le cas, et « notre dépôt » sans nom est la bonne réponse.
 */
export const DEPOT = lieu('depot')!;

/**
 * Les lieux qu'on a le droit de NOMMER sur une surface machine.
 *
 * Un lieu supposé n'y figure pas. La liste est vide-compatible : une sortie qui
 * la parcourt ne dit simplement rien du lieu manquant, au lieu d'inventer.
 */
export function lieuxConfirmes(): Lieu[] {
  return valeurDe('lieux').filter((l) => l.statut === 'verifie' && l.localite !== null);
}

/**
 * Pays desservis hors Belgique, pour les surfaces qui traitent la Belgique à
 * part — c'est le cas de toutes.
 */
export const PAYS_ETRANGERS: readonly string[] = valeurDe('couverture.pays').filter(
  (p) => p !== 'Belgique',
);
